import React from "react";
import { Box, Paper } from "@mui/material";
import { useTheme } from '@mui/material/styles';
import AppBar from "@mui/material/AppBar";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";

/**
 * HorizontalTabs – tab bar with optional premium variant.
 *
 * Premium variant UI/UX principles:
 * - Accessibility: nav + aria-label, focus-visible ring (keyboard), MUI tablist/tab roles
 * - Reduced motion: transitions and entrance animation disabled when prefers-reduced-motion
 * - Touch: 44px min height and scroll buttons; adequate tap targets
 * - Visual hierarchy: clear selected state (pill + border + shadow)
 * - Theme: full dark/light support
 */
function TabPanel(props) {
  const { children, useBox = false, index, value, ...other } = props;
  const item = useBox ? children : <Box p={3}>{children}</Box>;
  const isActive = value === index;
  return (
    <div
      role="tabpanel"
      hidden={!isActive}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {isActive ? (
        <Box
          sx={{
            "@keyframes tabPanelIn": {
              from: { opacity: 0, transform: "translateY(6px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
            "@media (prefers-reduced-motion: no-preference)": {
              animation: "tabPanelIn 0.25s ease-out",
            },
          }}
        >
          {item}
        </Box>
      ) : null}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export default function HorizontalTabs(props) {
  const {
    allowScrollButtonsMobile = true,
    ariaLabel = "Section tabs",
    customControl,
    customValue,
    fullWidth = true,
    headerStyle,
    isSnapshotPage,
    noAppBarColor,
    premium = false,
    position = "static",
    useBox,
    useIndicator = true,
    variant = 'standard'
  } = props;
  const theme = useTheme()
  const { palette = {} } = theme
  const { success = {}, primary = {}, background = {}, text = {}, mode } = palette
  const isDark = mode === "dark";

  // Brand colors (from MuiTheme brand palette)
  const navyColor = primary?.main || "#002843";
  const greenColor = success?.main || "#A0C46D";
  const greenLight = success?.light || "#b8d38f";
  const greenDark = "#7a9c4a";
  const bgPaper = background?.paper || "#ffffff";
  const bgDefault = background?.default || "#f8fafc";
  const textPrimary = text?.primary || (isDark ? "#ffffff" : "#1e293b");
  const textSecondary = text?.secondary || (isDark ? "#b8c9da" : "#64748b");
  const dividerColor = theme.palette?.divider || (isDark ? "rgba(168, 190, 210, 0.25)" : "#e2e8f0");

  const classes = {
    // App bar with navy background
    appBar: {
      backgroundColor: isDark ? "#032d42" : navyColor,
      boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.2)" : "0 1px 3px rgba(0,0,0,0.08)",
      borderBottom: "1px solid rgba(255,255,255,0.1)",
    },
    // Light app bar background (no AppBar color) - with subtle distinction
    noAppBarBackground: {
      backgroundColor: isDark ? bgPaper : "#fafbfc",
      borderBottom: isDark ? "1px solid rgba(148, 163, 184, 0.2)" : "1px solid #e2e8f0",
      boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.15)" : "0 1px 3px rgba(0,0,0,0.06)",
    },
    indicator: {
      backgroundColor: greenColor,
      height: "3px",
      borderRadius: "3px 3px 0 0",
    },
    noIndicator: {
      backgroundColor: "transparent",
    },
    root: {
      flexGrow: 1,
    },
    selected: {
      color: "white",
      "&:focus": {
        outline: "none",
      },
    },
    stickyHeaderBar: {
      backgroundColor: isDark ? bgPaper : "#fafbfc",
      borderBottom: isDark ? "1px solid rgba(148, 163, 184, 0.2)" : "1px solid #e2e8f0",
    },
    // Container styles
    tabsContainer: {
      minHeight: "52px",
      "& .MuiTabs-flexContainer": {
        gap: "0px",
      },
      "& .MuiTabs-scrollButtons": {
        color: "inherit",
        "&.Mui-disabled": {
          opacity: 0.3,
        },
      },
    },
    // Modern tab for light background (noAppBarColor) - with dark mode support
    modernTab: {
      textTransform: "none",
      fontWeight: 500,
      fontSize: "0.9rem",
      minHeight: "52px",
      padding: "14px 24px",
      transition: "all 0.2s ease-in-out",
      color: isDark ? greenColor : navyColor,
      opacity: 0.65,
      borderBottom: "3px solid transparent",
      marginBottom: "-1px",
      "&:hover": {
        backgroundColor: isDark ? "rgba(160, 196, 109, 0.15)" : "rgba(160, 196, 109, 0.1)",
        color: isDark ? greenColor : navyColor,
        opacity: 0.85,
      },
      "&.Mui-selected": {
        color: isDark ? greenColor : navyColor,
        fontWeight: 600,
        opacity: 1,
        backgroundColor: isDark ? "rgba(160, 196, 109, 0.1)" : "#ffffff",
      },
      "&:focus-visible": {
        outline: `2px solid ${greenColor}`,
        outlineOffset: "-2px",
      },
    },
    // Modern tab for dark (navy) background
    modernTabWithAppBar: {
      textTransform: "none",
      fontWeight: 500,
      fontSize: "0.875rem",
      minHeight: "52px",
      padding: "14px 24px",
      transition: "all 0.2s ease-in-out",
      color: "rgba(255,255,255,0.75)",
      borderBottom: "3px solid transparent",
      marginBottom: "-1px",
      borderRadius: "10px 10px 0 0",
      "&:hover": {
        backgroundColor: "rgba(255,255,255,0.08)",
        color: "#ffffff",
      },
      "&.Mui-selected": {
        color: "#ffffff",
        fontWeight: 600,
        backgroundColor: "transparent",
      },
      "&:focus-visible": {
        outline: "2px solid rgba(255,255,255,0.5)",
        outlineOffset: "-2px",
      },
    },
    // Snapshot page specific tab styles (green filled active state)
    snapshotTab: {
      textTransform: "none",
      fontWeight: 500,
      fontSize: "0.875rem",
      minHeight: "52px",
      padding: "14px 24px",
      transition: "all 0.2s ease-in-out",
      color: "rgba(255,255,255,0.8)",
      backgroundColor: "transparent",
      borderRadius: "8px 8px 0 0",
      margin: "0 2px",
      position: "relative",
      "&:hover": {
        backgroundColor: "rgba(255,255,255,0.1)",
        color: "#ffffff",
      },
      "&.Mui-selected": {
        color: "#ffffff",
        fontWeight: 600,
        backgroundColor: greenColor,
        boxShadow: "0 -2px 10px rgba(160, 196, 109, 0.3)",
      },
      "&:focus-visible": {
        outline: "2px solid rgba(255,255,255,0.5)",
        outlineOffset: "-2px",
      },
    },
    // Premium: pill-style tabs – best color scheme for light & dark (brand navy + green)
    premiumTab: {
      textTransform: "none",
      fontWeight: 500,
      fontSize: "0.8125rem",
      minHeight: "44px",
      padding: "10px 18px",
      margin: "0 3px",
      borderRadius: "10px",
      color: textSecondary,
      transition: "color 0.2s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      "&:hover": {
        color: isDark ? greenLight : navyColor,
        backgroundColor: isDark ? "rgba(160, 196, 109, 0.12)" : "rgba(0, 40, 67, 0.05)",
      },
      "&.Mui-selected": {
        color: isDark ? "#f1f5f9" : navyColor,
        fontWeight: 600,
        backgroundColor: isDark
          ? "rgba(160, 196, 109, 0.2)"
          : bgPaper,
        boxShadow: isDark
          ? "0 2px 10px rgba(0,0,0,0.3), 0 0 0 1px rgba(160, 196, 109, 0.35)"
          : "0 2px 6px rgba(0, 40, 67, 0.08), 0 0 0 1px rgba(0, 40, 67, 0.1), inset 0 1px 0 rgba(255,255,255,0.9)",
        border: isDark ? "1px solid rgba(160, 196, 109, 0.45)" : `1px solid ${dividerColor}`,
        ...(isDark ? {} : { borderLeft: `3px solid ${greenColor}` }),
      },
      "&:focus-visible": {
        outline: "none",
        boxShadow: `0 0 0 2px ${isDark ? "rgba(255,255,255,0.9)" : bgPaper}, 0 0 0 4px ${greenColor}`,
        outlineOffset: "2px",
      },
      "@media (prefers-reduced-motion: reduce)": {
        transition: "none",
      },
    },
    premiumContainer: {
      display: "inline-flex",
      alignItems: "center",
      padding: "6px",
      borderRadius: "14px",
      backgroundColor: isDark ? "#032d42" : "#f8fafc",
      border: isDark ? "1px solid rgba(160, 196, 109, 0.12)" : `1px solid ${dividerColor}`,
      boxShadow: isDark
        ? "0 4px 16px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.06)"
        : "0 1px 3px rgba(0, 40, 67, 0.06), inset 0 1px 0 rgba(255,255,255,1)",
      marginBottom: "16px",
      width: "100%",
      maxWidth: "100%",
      overflow: "hidden",
      "@media (prefers-reduced-motion: no-preference)": {
        animation: "premiumBarEnter 0.35s ease-out",
      },
      "@keyframes premiumBarEnter": {
        from: { opacity: 0, transform: "translateY(-6px)" },
        to: { opacity: 1, transform: "translateY(0)" },
      },
      "@media (prefers-reduced-motion: reduce)": {
        animation: "none",
      },
    },
    premiumTabsWrapper: {
      minHeight: "48px",
      flex: 1,
      "& .MuiTabs-flexContainer": {
        gap: "2px",
        flexWrap: "wrap",
      },
      "& .MuiTabs-scrollButtons": {
        color: "inherit",
        minWidth: 44,
        "&.Mui-disabled": { opacity: 0.3 },
      },
      "& .MuiTabs-indicator": {
        display: "none",
      },
    },
  }
  const [value, setValue] = React.useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const currentValue = customValue !== undefined ? customValue : value;

  // Determine which tab style to use
  const getTabStyle = (idx) => {
    if (premium) return classes.premiumTab;
    if (noAppBarColor) return classes.modernTab;
    if (isSnapshotPage) return classes.snapshotTab;
    return classes.modernTabWithAppBar;
  };

  const tabsSx = premium ? { ...classes.tabsContainer, ...classes.premiumTabsWrapper } : classes.tabsContainer;

  const headerContent = (
    <Tabs
      allowScrollButtonsMobile={allowScrollButtonsMobile}
      className={headerStyle ? headerStyle : ""}
      value={currentValue}
      onChange={customControl ? customControl : handleChange}
      sx={tabsSx}
      TabIndicatorProps={{
        style: premium || isSnapshotPage ? { display: "none" } : {
          backgroundColor: greenColor,
          height: "4px",
          borderRadius: "6px",
        },
      }}
      variant={variant}
    >
      {props.children
        .filter((element) => element !== null)
        .map((child, idx) => (
          <Tab
            {...a11yProps(idx)}
            sx={getTabStyle(idx)}
            icon={child.props.icon ? child.props.icon : null}
            key={idx}
            label={child.props.label}
            onClick={child.props.onClick}
          />
        ))}
    </Tabs>
  );

  const header = premium ? (
    <Box
      component="nav"
      aria-label={ariaLabel}
      sx={classes.premiumContainer}
    >
      {headerContent}
    </Box>
  ) : fullWidth && position === "sticky" ? (
    <AppBar
      position={position}
      sx={classes.stickyHeaderBar}
      elevation={0}
      size={12}
    >
      {headerContent}
    </AppBar>
  ) : fullWidth ? (
    <AppBar
      position={position}
      sx={!props.noAppBarColor ? classes.appBar : classes.noAppBarBackground}
      elevation={0}
    >
      {headerContent}
    </AppBar>
  ) : (
    headerContent
  );

  return (
    <div style={{ flexGrow: 1 }}>
      {header}
      {props.children
        .filter((element) => element !== null)
        .map((child, idx) => (
          <TabPanel
            index={idx}
            key={idx}
            useBox={useBox}
            value={customValue ? customValue : value}
          >
            {child}
          </TabPanel>
        ))}
    </div>
  );
}
